<template>
    <div class="flex justify-between items-center px-10 py-10 space-x-8">
        <div class="space-y-2">
            <h3 class="text-lg font-medium">Delete account</h3>
            <p class="text-sm text-gray-500">
                Account deletion will permanently close your account and erase all settings, personal data, and all partnership programs.
            </p>
        </div>
        <div>
            <modal title="Please confirm" size="small">
                <template #toggle>
                    <btn type="light">Delete</btn>
                </template>
                <template #body>
                    <validation-errors :errors="deleteError" class="mb-4"/>
                    <div class="space-y-6">
                        <div class="text-sm text-gray-500">
                            Are you sure you want to delete your account?
                            <br/>
                            You’ll have 30 days to restore it if you change your mind. After that, the account will be permanently deleted.”
                        </div>
                        <div class="grid gap-y-4">
                                <radio-group
                                    name="commission_type"
                                    id="commission_type"
                                    :options="deleteReasonOptions"
                                    v-model="predefinedDeleteReason"
                                ></radio-group>
                                <textarea v-if="predefinedDeleteReason === 'other'"
                                          v-model="customDeleteReason"
                                          id="delete-reason"
                                          rows="5"
                                          class="block w-full text-sm rounded border-gray-300 focus:ring-0 focus:border-gray-400 mb-1 scroll-h"
                                ></textarea>
                                <div>
                                    <label class="block text-gray-500 text-sm mb-1.5">Please type <span
                                        class="font-semibold">DELETE</span> to confirm.</label>
                                    <text-field v-model="confirmationText"
                                                v-on:keydown.enter="deleteAccount"
                                                v-on:keydown.enter.prevent="deleteAccount"
                                    />
                                </div>
                                <toggle title="Delete associated user with account"
                                        name="delete_user_with_account_deletion"
                                        v-model="deleteUserWithAccountDeletion"
                                />
                        </div>
                    </div>
                    <modal title="Notification" ref="accountDeleteWarningModal">
                        <template #body>
                            <div class="text-sm text-gray-500">
                                This user is associated with multiple accounts. Please delete the other accounts first, or uncheck the option to delete the user when deleting an account.                            </div>
                        </template>
                    </modal>
                </template>
                <template #footer>
                    <btn type="primary" :loading="loading" @click="deleteAccount">Yes, delete</btn>
                </template>
            </modal>
        </div>
    </div>
</template>
<script setup>

    import {ref} from "vue";
    import ValidationErrors from "../common/ValidationErrors.vue";
    import Toggle from "../../elements/forms/Toggle.vue";

    const props = defineProps({
        account: {
            type: Object,
            required: true
        },
        userAccountsCount: {
            type: Number,
            default: 0
        }
    });

    const deleteReasonOptions = [
        {value: 'I no longer run affiliate or referral programs', label: 'I no longer run affiliate or referral programs'},
        {value: 'I switched to a different solution', label: 'I switched to a different solution'},
        {value: 'The platform did not deliver the expected value or results', label: 'The platform did not deliver the expected value or results'},
        {value: 'The setup process was too complex or required more support', label: 'The setup process was too complex or required more support'},
        {value: 'The pricing does not align with our current budget', label: 'The pricing does not align with our current budget'},
        {value: 'I was testing the platform and no longer need access', label: 'I was testing the platform and no longer need access'},
        {value: 'other', label: 'Other (please specify)'},
    ];

    const deleteError = ref(null)
    const loading = ref(false)
    const confirmationText = ref('')
    const predefinedDeleteReason = ref(deleteReasonOptions[0].value)
    const customDeleteReason = ref('')
    const deleteUserWithAccountDeletion = ref(false)

    const accountDeleteWarningModal = ref(null);

    const deleteAccount = () => {

            if (loading.value) {
                return;
            }

            if (props.userAccountsCount > 1 && deleteUserWithAccountDeletion.value) {
                accountDeleteWarningModal.value.onShow();
                return;
            }

            loading.value = true;

            if (confirmationText.value.toLowerCase() !== 'delete') {
                deleteError.value = 'Confirmation text is incorrect.';
                loading.value = false;
                return;
            }
            axios
                .post(route('app_int.account.destroy_main_account', {account: props.account}), {
                    "confirmationText": confirmationText.value,
                    "deleteReason": predefinedDeleteReason.value === 'other' ? customDeleteReason.value : predefinedDeleteReason.value,
                    "deleteUserWithAccountDeletion": deleteUserWithAccountDeletion.value
                })
                .then(response => {
                    const status = parseInt(response.data.status || -1);
                    if (status === 1) {
                        const redirect_url = response.data.redirect_url || null;
                        if (redirect_url) {
                            window.location.href = redirect_url;
                        } else {
                            window.location.reload();
                        }
                    } else {
                        console.log(response.data.error)
                        deleteError.value = response.data?.error || '';
                    }
                })
                .catch(error => {
                    console.log(error.response)
                    if (error?.response?.status === 422) {
                        console.log(error.response.data.errors)
                        deleteError.value = error?.response?.data?.errors || error?.response?.data?.error ||  [];
                    } else if (error?.response?.status === 403) {
                        deleteError.value = error?.response?.data?.errors || error?.response?.data?.error ||  'Unauthorized action.';
                    } else {
                        deleteError.value = 'An unexpected error occurred. Please try again later.';
                    }
                })
                .finally(() => {
                    loading.value = false;
                });
   }
</script>
